import {
  Box,
  Stack,
  Typography,
  Divider,
  Button,
  SxProps,
  Theme,
  ButtonGroup,
} from "@mui/material";
import IncreaseDecrease from "../components/IncreaseDecrease";
import { ICompare, IOnClickMap } from "../types/types";
import { useMemo } from "react";
import { StyledTooltip } from "Components/UI/StyledTooltip";
import { ApiChartAttribute } from "@incendium/api";

interface ICellProps {
  dimensionName: string;
  valueFormatted: string;
  onClick?: IOnClickMap[];
  compare?: ICompare;
  headerName: string;
  isDimension?: boolean;
  sx?: SxProps<Theme>;
  rowFilters: ApiChartAttribute[];
}

function AnalyticsCell({
  dimensionName,
  valueFormatted,
  onClick,
  compare,
  headerName,
  isDimension,
  sx,
  rowFilters,
}: ICellProps) {
  const fontSize = useMemo(() => {
    const l = valueFormatted.length;
    if (l > 30) {
      return "subtitle2";
    }
    return "subtitle1";
  }, [valueFormatted]);

  const renderedBody = useMemo(() => {
    return (
      <Stack
        sx={{
          alignItems: isDimension ? "flex-start" : "center",
          height: "100%",
        }}
      >
        <Stack direction={"row"} spacing={0.25}>
          <Typography variant="body1" sx={sx || { fontSize: "13px" }}>
            {valueFormatted}
          </Typography>

          {compare && compare.value ? (
            <IncreaseDecrease
              direction={compare?.direction}
              value={compare.value}
              formatedValue={compare.formatedValue}
              useThemeColours
              fontSize={14}
              show="icon"
            />
          ) : (
            ""
          )}
        </Stack>
        {compare && compare.value ? (
          <Box>
            <IncreaseDecrease
              direction={compare?.direction}
              value={compare.value}
              formatedValue={compare.formatedValue}
              useThemeColours
              show="value"
            />
          </Box>
        ) : (
          ""
        )}
      </Stack>
    );
  }, [compare, isDimension, valueFormatted, sx]);

  if (!isDimension) {
    return (
      <StyledTooltip
        placement={valueFormatted.length > 100 ? "top" : "right"}
        wide={valueFormatted.length > 100}
        arrow
        enterDelay={700}
        disableTouchListener
        title={
          <Stack>
            <Stack px={2.5}>
              <Typography
                variant="subtitle2"
                color={"primary"}
                mt={0.5}
                gutterBottom
              >
                {dimensionName} - {headerName}
              </Typography>
              <Divider />
              <Stack alignItems={"center"} mt={1} mb={1.5}>
                <Typography
                  variant={fontSize}
                  fontWeight={500}
                  color={"inherit"}
                >
                  {valueFormatted}
                </Typography>
                {compare && compare.value ? (
                  <IncreaseDecrease
                    direction={compare?.direction}
                    value={compare.value}
                    formatedValue={compare.formatedValue}
                    useThemeColours
                    fontSize={14}
                  />
                ) : (
                  ""
                )}
              </Stack>
            </Stack>

            {(onClick || []).length > 0 && (
              <ButtonGroup orientation="vertical" variant="contained" fullWidth>
                {(onClick || []).map((oc) => (
                  <Button
                    key={oc.text}
                    size="extraSmall"
                    color="primary"
                    fullWidth
                    onClick={() => oc.fn(dimensionName, rowFilters)}
                    startIcon={oc.icon}
                  >
                    {oc.text}
                  </Button>
                ))}
              </ButtonGroup>
            )}
          </Stack>
        }
      >
        {renderedBody}
      </StyledTooltip>
    );
  }
  return (
    <StyledTooltip
      placement={valueFormatted.length > 100 ? "top" : "right"}
      arrow
      enterDelay={700}
      disableTouchListener
      title={
        <>
          <Stack px={2.5}>
            <Typography
              variant="subtitle2"
              color={"primary"}
              mt={0.5}
              gutterBottom
              sx={{ textWrap: "pretty" }}
            >
              {headerName}
            </Typography>
            <Divider />
            <Stack alignItems={"center"} mt={1} mb={1.5}>
              <Typography
                mt={1}
                variant={fontSize}
                color={"inherit"}
                fontWeight={500}
              >
                {valueFormatted}
              </Typography>
            </Stack>
          </Stack>
          {(onClick || []).length > 0 && (
            <ButtonGroup orientation="vertical" variant="contained" fullWidth>
              {(onClick || []).map((oc) => (
                <Button
                  key={oc.text}
                  size="extraSmall"
                  color="primary"
                  fullWidth
                  onClick={() => oc.fn(dimensionName, rowFilters)}
                  startIcon={oc.icon}
                >
                  {oc.text}
                </Button>
              ))}
            </ButtonGroup>
          )}
        </>
      }
    >
      {renderedBody}
    </StyledTooltip>
  );
}

export default AnalyticsCell;
